import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import { ChevronRight, Save, X, AlertCircle } from 'lucide-react';
import { useProviderEditorState, useUnsavedChanges } from './hooks';
import { useCreateProvider, useProviderFull, useUpdateProvider, useUpdateProviderEmail } from '../../hooks/useAdminProviders';
import { ProviderEditorTabs } from './ProviderEditorTabs';
import { SendWelcomeEmailButton } from '../SendWelcomeEmailButton';
import { LinkProvisionalContactButton } from '../manual-referrals/LinkProvisionalContactButton';
import { ManualReferralProfileButton } from '../manual-referrals/ManualReferralProfileButton';
import { useToast } from '@/shared/components/Toast';
import { extractApiError, type ApiErrorDetails } from '@/core/api/types';
import type { ProviderEditorFormData } from './schemas';
import type { UpdateProviderPayload, CreateProviderPayload } from '../../types/admin-provider.type';
import type { FieldErrors } from 'react-hook-form';
import {
  normalizeEditorSpecialty,
  toConfiguredCoveragePayload,
} from '@/shared/components/specialty-coverage';

type ProviderEditorPageProps = {
  mode: 'create' | 'edit';
  providerId?: string;
  onBack: () => void;
};

/**
 * Field name to human-readable label mapping
 */
const FIELD_LABELS: Record<string, string> = {
  'user.email': 'Email',
  'user.firstName': 'First Name',
  'user.lastName': 'Last Name',
  'user.profilePhoto': 'Profile Photo',
  'user.accountStatus': 'Account Status',
  'provider.companyName': 'Company Name',
  'provider.businessAddress': 'Business Address',
  'provider.latitude': 'Latitude',
  'provider.longitude': 'Longitude',
  'provider.vettingStatus': 'Vetting Status',
  'provider.phoneNumber': 'Phone Number',
  'provider.websiteUrl': 'Website URL',
  'provider.calendlyUrl': 'Calendly URL',
  'provider.serviceOfferings': 'Service Offerings',
  'provider.tagline': 'Tagline',
  'provider.providerBio': 'Bio',
  'provider.companyOverview': 'Company Overview',
  'provider.heroMediaUrl': 'Hero Media',
  'provider.mediaType': 'Media Type',
  'provider.introductionVideoUrl': 'Introduction Video',
  'provider.professionalTitle': 'Professional Title',
  'provider.yearsExperience': 'Years of Experience',
  'provider.yearsInBusiness': 'Years in Business',
  'provider.establishedYear': 'Established Year',
  'provider.targetAgeRange': 'Target Age Range',
  'provider.carePhilosophy': 'Care Philosophy',
  'provider.whyThisWork': 'Why This Work',
  'provider.continuingEducation': 'Continuing Education',
  'provider.hoursDescription': 'Hours Description',
  'provider.priceDetailsText': 'Price Details',
  'categoryIds': 'Categories',
  'specialties': 'Specialties & Coverage',
  'expertiseIds': 'Areas of Expertise',
  'payerOptionIds': 'Payment Methods',
  'availabilityScheduleId': 'Availability Schedule',
  'mediaGallery': 'Media Gallery',
};

/**
 * Format field path to human-readable label
 */
function getFieldLabel(fieldPath: string): string {
  return FIELD_LABELS[fieldPath] || fieldPath.split('.').pop()?.replace(/([A-Z])/g, ' $1').trim() || fieldPath;
}

/**
 * Format validation errors to human-readable messages
 */
function formatValidationErrors(errors: FieldErrors<ProviderEditorFormData>): string[] {
  const messages: string[] = [];

  const processErrors = (obj: any, prefix = '') => {
    Object.keys(obj).forEach((key) => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (value && typeof value === 'object') {
        // Check if it's a field error (has 'message' property)
        if ('message' in value && typeof value.message === 'string') {
          const label = getFieldLabel(fullPath);
          messages.push(`${label}: ${value.message}`);
        } else {
          // Recurse into nested objects
          processErrors(value, fullPath);
        }
      }
    });
  };

  processErrors(errors);
  return messages;
}

/**
 * ProviderEditorPage - Main container for creating/editing providers
 * 
 * Features:
 * - Create mode: Empty form for new providers
 * - Edit mode: Pre-populated form with existing provider data
 * - Unsaved changes warning
 * - Form validation
 * - Success/error toast notifications
 * 
 * Usage:
 * - Rendered within ProvidersTab based on viewMode state
 * - onBack callback returns to provider list
 */
export function ProviderEditorPage({ mode, providerId, onBack }: ProviderEditorPageProps) {
  const isEditMode = mode === 'edit';
  const { showToast } = useToast();
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isProviderFormHydrated, setIsProviderFormHydrated] = useState(!isEditMode);
  const [serverError, setServerError] = useState<ApiErrorDetails | null>(null);
  const [isPartialFailure, setIsPartialFailure] = useState(false);

  // Form state management with React Hook Form + Zod validation
  const form = useProviderEditorState();
  
  // Subscribe to form changes for real-time preview updates
  // Calling watch() at component level creates a subscription that triggers re-renders
  const formData = form.watch();

  // Fetch provider data in edit mode
  const { data: providerData, isLoading: isLoadingProvider, error: providerError } = useProviderFull(
    isEditMode ? providerId : undefined
  );

  // Mutations
  const createProvider = useCreateProvider();
  const updateProvider = useUpdateProvider();
  const updateProviderEmail = useUpdateProviderEmail();

  // Warn user about unsaved changes on navigation
  useUnsavedChanges(form.formState.isDirty);

  // Hide validation/server errors when user makes changes to form
  // Skip auto-clear when isPartialFailure is true (error stays until X button)
  useEffect(() => {
    if (showValidationErrors || (serverError && !isPartialFailure)) {
      const subscription = form.watch(() => {
        setShowValidationErrors(false);
        setServerError(null);
      });
      return () => subscription.unsubscribe();
    }
  }, [showValidationErrors, serverError, isPartialFailure, form]);

  useEffect(() => {
    if (isEditMode) {
      setIsProviderFormHydrated(false);
      return;
    }

    setIsProviderFormHydrated(true);
  }, [isEditMode, providerId]);

  // Which provider (and server-side vettingStatus) the form was last loaded from —
  // lets a background refetch keep the admin's unsaved edits (see reset below).
  const loadedProviderRef = useRef<{ id?: string; vettingStatus?: string }>({});

  // Load provider data into form when fetched
  useEffect(() => {
    if (isEditMode && providerData) {
      // Transform provider data to form format
      const formData: ProviderEditorFormData = {
        user: {
          email: providerData.user.email, // Read-only in edit mode
          firstName: providerData.user.firstName,
          lastName: providerData.user.lastName || '',
          profilePhoto: providerData.user.profilePhoto || '',
          accountStatus: providerData.user.accountStatus || 'ACTIVE',
        },
        provider: {
          companyName: providerData.companyName,
          businessAddress: providerData.businessAddress || '',
          hideBusinessAddress: providerData.hideBusinessAddress ?? false,
          latitude: providerData.latitude,
          longitude: providerData.longitude,
          vettingStatus: providerData.vettingStatus,
          newClientAcceptance: providerData.newClientAcceptance,
          isBquestPreferred: providerData.isBquestPreferred ?? false,
          phoneNumber: providerData.phoneNumber || '',
          websiteUrl: providerData.websiteUrl || '',
          calendlyUrl: providerData.calendlyUrl || '',
          serviceOfferings: providerData.serviceOfferings || '',
          tagline: providerData.tagline || '',
          providerBio: providerData.providerBio || '',
          companyOverview: providerData.companyOverview || '',
          priceRangeIndicator: (providerData.priceRangeIndicator as '$' | '$$' | '$$$' | '$$$$' | null) || null,
          priceDetailsText: providerData.priceDetailsText || '',
          heroMediaUrl: providerData.heroMediaUrl || '',
          mediaType: providerData.mediaType || '',
          introductionVideoUrl: providerData.introductionVideoUrl || '',
          professionalTitle: providerData.professionalTitle || '',
          yearsExperience: providerData.yearsExperience,
          yearsInBusiness: providerData.yearsInBusiness,
          establishedYear: providerData.establishedYear,
          certificationsArray: providerData.certificationsArray || [],
          professionalLicenses: providerData.professionalLicenses || [],
          awardsRecognitions: providerData.awardsRecognitions || [],
          languages: providerData.languages || [],
          professionalAssociations: providerData.professionalAssociations || [],
          payerSources: providerData.payerSources || [],
          serviceRanges: providerData.serviceRanges || [],
          targetAgeRange: providerData.targetAgeRange || '',
          carePhilosophy: providerData.carePhilosophy || '',
          whyThisWork: providerData.whyThisWork || '',
          continuingEducation: providerData.continuingEducation || '',
          emergencyResponseAvailable: providerData.emergencyResponseAvailable || false,
          emergencyResponseDetails: providerData.emergencyResponseDetails || '',
          hoursDescription: providerData.hoursDescription || '',
          placeId: providerData.placeId || '',
          socialHandles: providerData.socialHandles,
          membershipDate: providerData.membershipDate ? new Date(providerData.membershipDate) : null,
          renewalDate: providerData.renewalDate ? new Date(providerData.renewalDate) : null,
        },
        // Extract IDs from relationship arrays
        // Note: Backend returns junction table objects with nested reference data
        // We need the actual reference data IDs (cat.category.id), not junction IDs (cat.id)
        categoryIds: Array.isArray(providerData.categories) 
          ? providerData.categories.map(cat => cat.category?.id || cat.id).filter(Boolean)
          : [],
        specialties: Array.isArray(providerData.specialties)
          ? providerData.specialties
              .map((spec) => {
                // New editor shape (ticket 15) or legacy junction row.
                if ('coverageScope' in spec || 'specialtyId' in spec) {
                  return normalizeEditorSpecialty({
                    specialtyId: spec.specialtyId,
                    coverageScope: spec.coverageScope ?? null,
                    serviceRanges: spec.serviceRanges ?? [],
                    coverageStates: spec.coverageStates ?? [],
                  });
                }
                const legacyId =
                  (spec as { specialty?: { id?: string }; id?: string }).specialty?.id ||
                  (spec as { id?: string }).id;
                return normalizeEditorSpecialty({
                  specialtyId: legacyId || '',
                  coverageScope: null,
                  serviceRanges: [],
                  coverageStates: [],
                });
              })
              .filter((item) => item.specialtyId)
          : [],
        expertiseIds: Array.isArray(providerData.expertise)
          ? providerData.expertise.map(exp => exp.expertise?.id || exp.id).filter(Boolean)
          : [],
        payerOptionIds: Array.isArray(providerData.payerOptions)
          ? providerData.payerOptions.map(payer => payer.payerOption?.id || payer.id).filter(Boolean)
          : [],
        availabilityScheduleId: Array.isArray(providerData.availabilitySchedules) && providerData.availabilitySchedules.length > 0
          ? (providerData.availabilitySchedules[0].availabilitySchedule?.id || providerData.availabilitySchedules[0].id)
          : '',
        // Media gallery items for preview sync
        mediaGallery: providerData.media ? providerData.media.map(item => ({
          id: item.id,
          mediaType: item.mediaType as 'image' | 'video',
          mediaUrl: item.mediaUrl,
          caption: item.caption || undefined,
          isPrimary: item.isPrimary,
          displayOrder: item.displayOrder,
        })) : [],
        // Services catalog items for form state
        servicesCatalog: providerData.services ? providerData.services.map(service => ({
          id: service.id,
          serviceName: service.serviceName,
          serviceDescription: service.serviceDescription || '',
          servicePrice: service.servicePrice || '',
          priceType: service.priceType || '',
          priceRangeIndicator: service.priceRangeIndicator as '$' | '$$' | '$$$' | '$$$$' | null,
          isActive: service.isActive,
          displayOrder: service.displayOrder,
        })) : [],
        // Registered states (where provider is licensed)
        registeredStateIds: Array.isArray(providerData.registeredStates)
          ? providerData.registeredStates.map(rs => rs.stateCode).filter(Boolean)
          : [],
      };

      // A refetch of the SAME provider (e.g. after a vetting decline refreshes the
      // provider cache) must not wipe unsaved edits: keepDirtyValues updates only the
      // fields the admin hasn't touched. A different provider always resets fully.
      const sameProvider = loadedProviderRef.current.id === providerData.id;
      form.reset(formData, sameProvider ? { keepDirtyValues: true } : undefined);

      // The server's vettingStatus is authoritative when IT changed (decline sets
      // REJECTED) — apply it even if the status field was dirty, or the next save
      // would re-submit the stale status and silently revert the rejection.
      if (sameProvider && loadedProviderRef.current.vettingStatus !== providerData.vettingStatus) {
        form.setValue('provider.vettingStatus', providerData.vettingStatus);
      }
      loadedProviderRef.current = { id: providerData.id, vettingStatus: providerData.vettingStatus };

      setIsProviderFormHydrated(true);
    }
  }, [isEditMode, providerData, form]);

  /**
   * Handle inline field update from Preview tab
   * 
   * Features:
   * - Updates individual field in form immediately
   * - Saves to backend
   * - Form.watch() updates to reflect new value in Preview
   * - Shows success/error toast
   * 
   * @param fieldName - Dot-notation field path (e.g., "provider.companyName", "user.firstName")
   * @param value - New field value
   */
  const handleFieldUpdate = async (fieldName: string, value: string) => {
    if (!isEditMode || !providerId) {
      throw new Error('Inline edit only available in edit mode');
    }

    // Store original value for rollback on error
    const originalValue = form.getValues(fieldName as any);

    try {
      // 1. Update form field FIRST (so form.watch() returns new value immediately)
      form.setValue(fieldName as any, value, { 
        shouldDirty: true,      // Mark form as having changes
        shouldValidate: true,   // Trigger validation
        shouldTouch: true,      // Mark field as touched
      });

      // 2. Prepare partial update payload with just this field
      const payload: UpdateProviderPayload = {};
      
      // Map field name to payload structure
      if (fieldName.startsWith('user.')) {
        const userField = fieldName.replace('user.', '') as keyof NonNullable<UpdateProviderPayload['user']>;
        payload.user = { [userField]: value } as UpdateProviderPayload['user'];
      } else if (fieldName.startsWith('provider.')) {
        const providerField = fieldName.replace('provider.', '') as keyof NonNullable<UpdateProviderPayload['provider']>;
        payload.provider = { [providerField]: value } as UpdateProviderPayload['provider'];
      } else {
        // Direct field name (backward compatibility)
        payload.provider = { [fieldName]: value } as UpdateProviderPayload['provider'];
      }

      // 3. Save to backend
      await updateProvider.mutateAsync({
        id: providerId,
        payload,
      });

      // Success - form is already updated, Preview will show new value via form.watch()
      showToast('Field updated successfully', 'success', 2000);
    } catch (error) {
      // Handle errors - revert form value on failure
      form.setValue(fieldName as any, originalValue);
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to update field. Please try again.';
      
      showToast(errorMessage, 'error', 4000);
      throw error; // Re-throw so inline edit component stays in edit mode
    }
  };

  /**
   * Handle form submission
   * 
   * Features:
   * - Shows success toast on save
   * - Detects Kinde account status errors and shows specific message
   * - Detects Kinde custom properties sync warnings (non-blocking)
   * - Shows generic error for other failures
   * - Preserves form data on error (no reset)
   */
  const handleSubmit = async (data: ProviderEditorFormData) => {
    try {
      setServerError(null);
      setIsPartialFailure(false);
      let result;

      if (isEditMode && providerId) {
        const { email, ...userWithoutEmail } = data.user;

        const emailChanged =
          !!providerData?.user?.email &&
          email.toLowerCase().trim() !== providerData.user.email.toLowerCase().trim();

        // Transform single availabilityScheduleId to array for backend
        const availabilityScheduleIds = data.availabilityScheduleId
          ? [data.availabilityScheduleId]
          : [];

        const payload: UpdateProviderPayload = {
          user: userWithoutEmail,
          provider: data.provider,
          categoryIds: data.categoryIds,
          specialties: toConfiguredCoveragePayload(data.specialties),
          expertiseIds: data.expertiseIds,
          payerOptionIds: data.payerOptionIds,
          availabilityScheduleIds,
          registeredStateIds: data.registeredStateIds,
          ...(data.servicesCatalog && data.servicesCatalog.length > 0 && {
            services: data.servicesCatalog.map(service => ({
              id: service.isNew ? undefined : service.id,
              serviceName: service.serviceName,
              serviceDescription: service.serviceDescription || null,
              servicePrice: service.servicePrice || null,
              priceType: service.priceType || null,
              priceRangeIndicator: service.priceRangeIndicator || null,
              isActive: service.isActive,
              displayOrder: service.displayOrder,
            })),
          }),
          media: data.mediaGallery || [],
        };

        // Fire both requests in parallel
        const results = await Promise.allSettled([
          updateProvider.mutateAsync({ id: providerId, payload }),
          ...(emailChanged
            ? [updateProviderEmail.mutateAsync({ id: providerId, email })]
            : []),
        ]);

        // Collect errors from any failed requests
        const errors: ApiErrorDetails[] = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r) => extractApiError(r.reason));

        if (errors.length > 0) {
          const someSucceeded = results.some((r) => r.status === 'fulfilled');
          const mergedMessage = errors.map((e) => e.message).join('. ');
          const mergedFieldErrors = errors.flatMap((e) => e.fieldErrors);
          setServerError({ message: mergedMessage, fieldErrors: mergedFieldErrors });
          setIsPartialFailure(someSucceeded);
          console.error('Failed to save provider:', errors);
          return;
        }

        result = (results[0] as PromiseFulfilledResult<unknown>).value;
      } else {
        // Create mode — email is included in the create payload, no separate call
        const { mediaGallery, servicesCatalog, availabilityScheduleId, specialties, ...restData } = data;

        const availabilityScheduleIds = availabilityScheduleId
          ? [availabilityScheduleId]
          : [];

        const createPayload: CreateProviderPayload = {
          ...restData,
          specialtyIds: specialties.map((item) => item.specialtyId),
          availabilityScheduleIds,
        };

        result = await createProvider.mutateAsync(createPayload);
      }

      // Check for Kinde custom properties sync warnings (non-blocking)
      const resultWithWarnings = result as { warnings?: string[] } | undefined;
      if (resultWithWarnings?.warnings && resultWithWarnings.warnings.length > 0) {
        const hasCustomPropertiesWarning = resultWithWarnings.warnings.some(
          (warning: string) =>
            warning.includes('custom properties') ||
            warning.includes('token properties') ||
            warning.includes('JWT'),
        );

        if (hasCustomPropertiesWarning) {
          showToast(
            'Provider saved successfully, but token properties sync failed. User may need to re-login to see updated token claims.',
            'info',
            6000,
          );
        } else {
          showToast(
            `Provider ${isEditMode ? 'updated' : 'created'} successfully with warnings. Check logs for details.`,
            'info',
            5000,
          );
        }
      } else {
        showToast(`Provider ${isEditMode ? 'updated' : 'created'} successfully`, 'success');
      }

      onBack();
    } catch (error) {
      const details = extractApiError(error);
      setServerError(details);
      console.error('Failed to save provider:', error);
    }
  };

  /**
   * Handle cancel - go back without saving
   */
  const handleCancel = () => {
    onBack();
  };

  /**
   * Handle save button click
   * 
   * Features:
   * - Validates form before submission
   * - Shows validation errors if form is invalid
   * - Proceeds with submission if form is valid
   */
  const handleSaveClick = async () => {
    if (isSaving) return; // Prevent double-clicks

    // Hide previous validation errors
    setShowValidationErrors(false);

    // Trigger form validation
    const isValid = await form.trigger();

    if (!isValid) {
      // Form is invalid - show validation errors
      setShowValidationErrors(true);
      return;
    }

    // Form is valid - proceed with submission
    setIsSaving(true);
    try {
      const formData = form.getValues();
      await handleSubmit(formData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...form}>
      <div className="flex min-h-screen flex-col bg-background">
        {/* Breadcrumb Navigation */}
        <nav className="border-b border-gray-200 bg-white px-6 py-4" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/admin" className="hover:text-gray-700">
              Admin
            </Link>
          </li>
          <ChevronRight className="h-4 w-4" />
          <li>
            <button
              type="button"
              onClick={onBack}
              className="hover:text-gray-700"
            >
              Providers
            </button>
          </li>
          <ChevronRight className="h-4 w-4" />
          <li className="text-gray-900 font-medium">
            {isEditMode ? 'Edit' : 'Create'}
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? 'Edit Provider' : 'Create Provider'}
              </h1>

              {/* Profile Visible Indicator */}
              {isEditMode && isProviderFormHydrated && formData && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Profile Visible:</span>
                  {formData.provider?.vettingStatus === 'APPROVED' && formData.user?.accountStatus === 'ACTIVE' ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                      True
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
                      False
                    </span>
                  )}
                </div>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {isEditMode
                ? 'Update service provider profile information'
                : 'Create a new service provider profile'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-end space-y-3">
            <div className="flex items-center space-x-3">
              {/* Send Welcome Email Button (T075 - US8) - Only in edit mode for professionals */}
              {isEditMode && providerData?.user && (
                <SendWelcomeEmailButton
                  userId={providerData.user.id}
                  userRole="SERVICE_PROVIDER"
                  userEmail={providerData.user.email}
                />
              )}
              {isEditMode && providerData?.user && (
                <ManualReferralProfileButton
                  referredEntity={{
                    id: providerData.user.id,
                    kind: 'USER',
                    label: providerData.companyName,
                    email: providerData.user.email,
                    role: 'SERVICE_PROVIDER',
                    organizationName: providerData.companyName,
                  }}
                />
              )}
              {isEditMode && providerData?.user && (
                <LinkProvisionalContactButton
                  userId={providerData.user.id}
                  role="SERVICE_PROVIDER"
                  accountLabel={providerData.companyName}
                />
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#026857] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </button>
              <button
                type="button"
                disabled={isSaving}
                onClick={handleSaveClick}
                className="inline-flex items-center rounded-md border border-transparent bg-[#026857] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#025246] focus:outline-none focus:ring-2 focus:ring-[#026857] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                )}
              </button>
            </div>

            {/* Validation Errors */}
            {showValidationErrors && Object.keys(form.formState.errors).length > 0 && (
              <div className="rounded-md bg-red-50 border border-red-200 p-4 max-w-2xl">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Please fix the following errors before saving:
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul className="list-disc list-inside space-y-1">
                        {formatValidationErrors(form.formState.errors).map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Server Error */}
            {serverError && (
              <div className="rounded-md bg-red-50 border border-red-200 p-4 max-w-2xl" role="alert">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                      {serverError.message}
                    </h3>
                    {serverError.fieldErrors.length > 0 && (
                      <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                        {serverError.fieldErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setServerError(null);
                      setIsPartialFailure(false);
                    }}
                    className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                    aria-label="Dismiss error"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 px-6 py-6" role="main">
        <div className="mx-auto max-w-6xl">
          {/* Error State */}
          {isEditMode && providerError && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Failed to load provider
              </h3>
              <p className="text-gray-600 mb-4">
                {providerError.message || 'An error occurred while loading the provider data'}
              </p>
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
              >
                Back to List
              </button>
            </div>
          )}

          {/* Loading State */}
          {isEditMode && isLoadingProvider && !providerError && (
            <div data-testid="provider-editor-skeleton" className="space-y-4">
              <div className="h-12 w-full animate-pulse rounded-lg bg-gray-200" />
              <div className="h-12 w-full animate-pulse rounded-lg bg-gray-200" />
              <div className="h-96 w-full animate-pulse rounded-lg bg-gray-200" />
            </div>
          )}

          {/* Form Content */}
          {(!isEditMode || (!isLoadingProvider && !providerError && providerData)) && (
            <ProviderEditorTabs
              control={form.control}
              errors={form.formState.errors}
              formData={formData}
              isDirty={form.formState.isDirty}
              userId={providerData?.user?.id}
              kindeId={providerData?.user?.kindeId || undefined}
              intakeFormUrl={providerData?.intakeFormUrl}
              providerId={isEditMode ? providerId : undefined}
              onFieldUpdate={handleFieldUpdate}
              setValue={form.setValue}
              serverFieldErrors={serverError?.fieldErrors ?? []}
            />
          )}
        </div>
      </main>
      </div>
    </FormProvider>
  );
}

